import { type Context, Controller } from '@hotwired/stimulus';
import { slugify } from 'src/slugify';

export default class SiteFormController extends Controller {
  static targets = ['name', 'slug', 'distinguishedName'];

  declare private nameTarget: HTMLInputElement;
  declare private slugTarget: HTMLInputElement;
  declare private parentTarget: HTMLInputElement;
  declare private distinguishedNameTarget: HTMLInputElement;

  private parentDn: string = '';

  public constructor(context: Context) {
    super(context);
  }

  private get slug(): string {
    return this.slugTarget.value;
  }

  private set slug(newValue: string) {
    this.slugTarget.value = newValue;
  }

  public connect() {
    const parts = this.distinguishedNameTarget.value.split('.');
    parts.pop();

    this.parentDn = parts.join('.');
  }

  public onNameChanged(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    const newValue = input.value;

    if (newValue.length > 30) {
      this.regenerateSlug(
        newValue.slice(0, 15) +
          newValue.slice(newValue.length - 15, newValue.length),
      );
    } else {
      this.regenerateSlug(newValue);
    }
  }

  public onRegenerateSlug(): void {
    const newValue = this.nameTarget.value;

    if (newValue.length > 30) {
      this.regenerateSlug(
        newValue.slice(0, 15) +
          newValue.slice(newValue.length - 15, newValue.length),
      );
    } else {
      this.regenerateSlug(newValue);
    }
  }

  private regenerateSlug(newValue: string): void {
    if (/[0-9-]/.test(newValue[0])) {
      newValue = `slug-${newValue}`;
    }

    this.slug = slugify(newValue, {
      lower: true,
      trim: true,
      remove: /[^A-Za-z0-9\s-]/g,
    });

    this.updateDistinguishedName(this.slug);
  }

  public onSlugChanged(event: InputEvent): void {
    const input = event.currentTarget as HTMLInputElement;
    const newSlug = input.value;

    this.updateDistinguishedName(newSlug);
  }

  private updateDistinguishedName(slug: string): void {
    const input = this.distinguishedNameTarget;

    input.value = [this.parentDn, slug].filter(Boolean).join('.');

    if (input.classList.contains('--error')) {
      input.classList.remove('--error');
      input.parentElement?.classList.remove('--error');
    }

    // Check validity
    const maxLength = input.maxLength;
    if (maxLength > 0 && input.value.length > maxLength) {
      input.classList.add('--error');
      input.parentElement?.classList.add('--error');

      const output =
        input.parentElement?.parentElement?.querySelector<HTMLOutputElement>(
          '[data-target="error"]',
        );

      if (output) {
        output.textContent = `The length of the distinguished name must be ${maxLength} characters or less.`;
      }
    }
  }
}
